import { cn } from '@/lib/cn';
import { Button } from './button';

export interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions?: number[];
  className?: string;
  isLoading?: boolean;
  showBorder?: boolean;
  showRange?: boolean;
  showPageSize?: boolean;
  compact?: boolean;
}

export function Pagination({
  page,
  pageSize,
  total,
  totalPages,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  className,
  isLoading = false,
  showBorder = true,
  showRange = true,
  showPageSize = true,
  compact = false,
}: PaginationProps) {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  // Generate page numbers with ellipsis
  const getPageNumbers = (): (number | '...')[] => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (page <= 3) {
      return [1, 2, 3, 4, '...', totalPages];
    }
    if (page >= totalPages - 2) {
      return [1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [1, '...', page - 1, page, page + 1, '...', totalPages];
  };

  const pages = getPageNumbers();

  const idSuffix = compact ? '-top' : '-bottom';

  return (
    <div
      className={cn(
        'flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6',
        showBorder && 'border-t border-gray-200 bg-white',
        compact && 'py-2 px-0 bg-transparent border-none sm:px-0 sm:justify-end',
        className,
      )}
    >
      {/* Left side: Range information & Page Size dropdown */}
      {(!compact || showRange || showPageSize) && (
        <div className="flex flex-wrap items-center justify-between gap-4 sm:justify-start">
          {showRange && (
            <p className="text-xs text-gray-600">
              Showing <span className="font-semibold text-gray-900">{from}</span> to{' '}
              <span className="font-semibold text-gray-900">{to}</span> of{' '}
              <span className="font-semibold text-gray-900">{total}</span> entries
            </p>
          )}

          {showPageSize && (
            <div className="flex items-center gap-1.5">
              <label htmlFor={`page-size-select${idSuffix}`} className="text-xs font-medium text-gray-600">
                Show:
              </label>
              <select
                id={`page-size-select${idSuffix}`}
                value={pageSize}
                disabled={isLoading}
                onChange={(e) => {
                  const newSize = Number(e.target.value);
                  onPageSizeChange(newSize);
                }}
                className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:opacity-50"
              >
                {pageSizeOptions.map((option) => (
                  <option key={option} value={option}>
                    {option} / page
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Right side: Page navigation buttons */}
      <div className="flex items-center justify-center gap-1 sm:justify-end">
        <Button
          variant="outline"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1 || isLoading}
          className="h-7 px-2 text-xs font-medium shadow-sm"
        >
          Previous
        </Button>

        <div className="flex items-center gap-1">
          {pages.map((p, idx) => {
            if (p === '...') {
              return (
                <span
                  key={`ellipsis-${idx}`}
                  className="px-1.5 text-xs text-gray-400 select-none"
                >
                  …
                </span>
              );
            }
            return (
              <button
                key={p}
                onClick={() => onPageChange(p)}
                disabled={isLoading}
                className={cn(
                  'h-7 min-w-[1.75rem] rounded-md px-2 text-xs font-medium transition-colors shadow-sm',
                  p === page
                    ? 'bg-brand-700 text-white font-semibold shadow-inner'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50',
                )}
              >
                {p}
              </button>
            );
          })}
        </div>

        <Button
          variant="outline"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages || totalPages === 0 || isLoading}
          className="h-7 px-2 text-xs font-medium shadow-sm"
        >
          Next
        </Button>
      </div>
    </div>
  );
}
